import React, { MutableRefObject, useImperativeHandle } from "react";
import { Nullable } from "global";
import { Box } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputField, SelectField } from "components/form";
import { RenderWithFallback } from "components/shared/Conditional/RenderWithFallback";
import { LoadingFallback } from "../LoadingFollback";
import { EquipmentDelivery } from "../../../types";
import { equipmentDeliverySchema } from "../../../model";
import { useEquipmentDelivery } from "../../../hooks/useEquipmentDelivery";
import { initialDeliveryFormValue, usStatesOptions } from "../../../constants";

interface Props {
  equipmentId: string | undefined;
  submitRef?: MutableRefObject<
    Nullable<{
      submit: () => Promise<{ isValid: boolean; formValues: EquipmentDelivery }>;
    }>
  >;
}

export const EquipmentDeliveryForm = ({ equipmentId, submitRef }: Props) => {
  const { control, getValues, trigger, reset } = useForm<EquipmentDelivery>({
    mode: "all",
    defaultValues: initialDeliveryFormValue,
    resolver: zodResolver(equipmentDeliverySchema),
  });

  const { isLoading } = useEquipmentDelivery(equipmentId, reset);

  useImperativeHandle(submitRef, () => ({
    submit: async () => {
      const isValid = await trigger();
      return { isValid, formValues: getValues() };
    },
  }));

  return (
    <RenderWithFallback isLoading={isLoading} fallBack={<LoadingFallback />}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "1rem" }}>
        <InputField name="country" label="Country" control={control} inputProps={{ variant: "outlined" }} />

        <SelectField label="State" control={control} name="state" options={usStatesOptions} variant="outlined" />

        <InputField label="City" control={control} name="city" inputProps={{ variant: "outlined" }} />

        <InputField label="Address" control={control} name="streetAddress" inputProps={{ variant: "outlined" }} />

        <InputField control={control} name="postalCode" label="Postal Code" inputProps={{ variant: "outlined" }} />
      </Box>
    </RenderWithFallback>
  );
};
